/*
 * CommunityManagementTab — DEV-1190 Phase 1.
 *
 * Operational admin surface for a Geo or Org community. Lives as a
 * top-level tab on CommunityDetailPage, peer to Alignment / News /
 * Feed / Health / Forum / Media. Distinct from CommunitySettingsModal,
 * which is configuration. Mirrors the Projects pattern (Settings vs
 * Management split).
 *
 * Visual consistency note (2026-06-01): the sub-tab navigation uses
 * the shared <WorkspaceCards /> primitive — the same icon-card pattern
 * used by Project Management. This is deliberate: Project Management
 * and Community Management serve the same conceptual purpose
 * (operational admin → select-an-aspect), so the UX should rhyme to
 * lower cognitive load for admins moving between surfaces.
 *
 * Sub-tab availability by pillar (locked 2026-06-01):
 *
 *   Sub-tab               | Geo community | Org community
 *   ----------------------+---------------+--------------
 *   Team & Roles          | YES           | YES
 *   Priorities            | YES           | YES
 *   Emergency Response    | YES           | YES
 *   Structures            | NO            | YES   (orgs author sub-communities + collections)
 *   Metadata              | NO            | YES   (orgs author custom schemas; geos use the fixed geographic taxonomy)
 *   Campaign Designer     | YES (future)  | YES (future)
 *
 * Geo communities (countries, states, cities) inhabit a fixed
 * geographic taxonomy. They don't create their own sub-communities,
 * and they don't author custom metadata schemas — those are
 * organization-specific authoring activities. Showing Structures /
 * Metadata on a geo community would be misleading and confusing.
 *
 * Visibility of the tab itself is gated by community_entities.
 * management_visibility (public | community | team) — that gate lives
 * in CommunityDetailPage. This component assumes it's already been
 * cleared to render.
 *
 * Write actions inside any sub-tab are gated by V2 Community Owner /
 * Admin grant; visibility controls READ. Sysadmins always edit.
 */

import React, { useState, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import UnifiedTeamTab from '../UnifiedTeamTab/UnifiedTeamTab';
import CommunityMembershipTab from '../CommunityMembershipTab/CommunityMembershipTab';
import CommunityPlaybooksManagement from '../CommunityPlaybooksManagement/CommunityPlaybooksManagement';
import WorkspaceCards, {
  type WorkspaceCardItem,
} from '../WorkspaceCards/WorkspaceCards';
import styles from './CommunityManagementTab.module.css';

export type CommunityManagementSubTab =
  | 'team'
  | 'membership'
  | 'priorities'
  | 'emergency'
  | 'structures'
  | 'metadata'
  | 'playbooks'
  | 'campaigns';

export interface CommunityManagementTabProps {
  communityGuid: string;
  /** Geo vs org community drives some downstream copy + behavior. */
  pillarType: 'geo_community' | 'org_community';
  /** Whether the current viewer has admin rights on this community.
   *  Phase 1 only uses this to gate the inline edit-state placeholder
   *  copy; sub-tabs handle their own permission checks. */
  isAdmin: boolean;
  /** DEV-1414: display name + viewer guid for the Membership sub-tab
   *  (org-only). Optional so geo callers don't have to thread them. */
  communityName?: string;
  currentUserGuid?: string;
}

/* ------------------------------------------------------------------ */
/*  SVG icons — match the stroke style used by WorkspaceCards          */
/* ------------------------------------------------------------------ */

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const TeamIcon = () => (
  <svg {...iconProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/** ID badge — Membership sub-tab (DEV-1414, org-only). Sits beside Team so
 *  the belonging-vs-roles split reads in the nav itself (§8.7). */
const MembershipIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="2" />
    <path d="M15 8h4" />
    <path d="M15 12h4" />
    <path d="M5 16c.8-1.5 2.3-2 4-2s3.2.5 4 2" />
  </svg>
);

/** Star — top priority symbol; Priorities sub-tab. */
const PrioritiesIcon = () => (
  <svg {...iconProps}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

/** Life buoy — emergency response. Round, distinct, recognizable. */
const EmergencyIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
    <line x1="14.83" y1="9.17" x2="18.36" y2="5.64" />
    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
  </svg>
);

/** Org chart / hierarchy — Structures (sub-communities + collections). */
const StructuresIcon = () => (
  <svg {...iconProps}>
    <rect x="9" y="2" width="6" height="5" rx="1" />
    <rect x="2" y="17" width="6" height="5" rx="1" />
    <rect x="9" y="17" width="6" height="5" rx="1" />
    <rect x="16" y="17" width="6" height="5" rx="1" />
    <path d="M12 7v4" />
    <path d="M5 17v-2h14v2" />
    <path d="M12 13v4" />
  </svg>
);

/** Tag — Metadata (custom schemas + fields). */
const MetadataIcon = () => (
  <svg {...iconProps}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

/** Open book — Playbooks (author/govern published playbooks). */
const PlaybooksIcon = () => (
  <svg {...iconProps}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

/** Megaphone — Campaign Designer (future). */
const CampaignsIcon = () => (
  <svg {...iconProps}>
    <polygon points="3 11 3 13 8 13 13 17 13 7 8 11 3 11" />
    <path d="M16 8a5 5 0 0 1 0 8" />
    <path d="M19 5a9 9 0 0 1 0 14" />
  </svg>
);

const DEFAULT_SUB_TAB: CommunityManagementSubTab = 'team';

const CommunityManagementTab: React.FC<CommunityManagementTabProps> = ({
  communityGuid,
  pillarType,
  isAdmin,
  communityName,
  currentUserGuid,
}) => {
  const { t } = useAppTranslation('common');
  const [subTab, setSubTab] = useState<CommunityManagementSubTab>(DEFAULT_SUB_TAB);

  // Sub-tab list. Structures + Metadata are org-only — geo communities
  // inhabit a fixed geographic taxonomy and don't author their own
  // sub-communities or custom schemas. See the header doc-comment.
  const isOrg = pillarType === 'org_community';
  const cardItems = useMemo<WorkspaceCardItem[]>(() => {
    const items: WorkspaceCardItem[] = [
      {
        key: 'team',
        label: t('communityManagement.subTabTeam'),
        icon: <TeamIcon />,
      },
    ];
    if (isOrg) {
      // DEV-1414 (§8.7): Membership is ORG-ONLY — geo communities are
      // self-join and keep the Audience tab; the divergence is deliberate.
      // Placed directly beside Team: Team = roles/authority, Membership =
      // belonging (audience layer). Never render role controls here.
      items.push({
        key: 'membership',
        label: t('communityManagement.subTabMembership'),
        icon: <MembershipIcon />,
      });
    }
    items.push(
      {
        key: 'priorities',
        label: t('communityManagement.subTabPriorities'),
        icon: <PrioritiesIcon />,
      },
      {
        key: 'emergency',
        label: t('communityManagement.subTabEmergency'),
        icon: <EmergencyIcon />,
      },
    );
    if (isOrg) {
      items.push({
        key: 'structures',
        label: t('communityManagement.subTabStructures'),
        icon: <StructuresIcon />,
      });
      items.push({
        key: 'metadata',
        label: t('communityManagement.subTabMetadata'),
        icon: <MetadataIcon />,
      });
      // Playbooks are org-owned authoring artifacts (like Structures /
      // Metadata) — geo communities don't publish them.
      items.push({
        key: 'playbooks',
        label: t('communityManagement.subTabPlaybooks'),
        icon: <PlaybooksIcon />,
      });
    }
    items.push({
      key: 'campaigns',
      label: t('communityManagement.subTabCampaigns'),
      icon: <CampaignsIcon />,
      disabled: true,
    });
    return items;
  }, [t, isOrg]);

  // Defensive: if the active sub-tab gets hidden when pillar changes
  // (e.g. user navigates from an org to a geo community on the same
  // mount), bounce back to the default. Use a memo to compute the
  // active key on every render rather than syncing into useEffect —
  // simpler and avoids a transient render with an invalid key.
  const validKeys = useMemo(
    () => new Set(cardItems.map(c => c.key)),
    [cardItems],
  );
  const activeSubTab: CommunityManagementSubTab = validKeys.has(subTab)
    ? subTab
    : DEFAULT_SUB_TAB;

  return (
    <div className={styles.container}>
      {/* Header strip retired 2026-06-01 — the top-level "Management" tab
          label is sufficient identity; an in-tab title was redundant. */}

      {/* Sub-tab strip — shared WorkspaceCards primitive (same component
          used by Project Management). Consistency by construction. */}
      <WorkspaceCards
        items={cardItems}
        activeKey={activeSubTab}
        onSelect={(key) => setSubTab(key as CommunityManagementSubTab)}
      />

      {/* Sub-tab body */}
      <div className={styles.body}>
        {activeSubTab === 'team' && (
          <UnifiedTeamTab
            entityGuid={communityGuid}
            pillarType={pillarType}
            viewMode="community"
            isAdmin={isAdmin}
          />
        )}

        {activeSubTab === 'membership' && isOrg && (
          <CommunityMembershipTab
            communityGuid={communityGuid}
            communityName={communityName || ''}
            currentUserGuid={currentUserGuid || ''}
            isAdmin={isAdmin}
          />
        )}

        {activeSubTab === 'priorities' && (
          <div className={styles.placeholder}>
            <h3 className={styles.placeholderTitle}>
              {t('communityManagement.prioritiesTitle')}
            </h3>
            <p className={styles.placeholderText}>
              {t('communityManagement.prioritiesDescription')}
            </p>
            <p className={styles.placeholderPhase}>
              {t('communityManagement.phasePlaceholder', { defaultValue: 'Phase 2.' })}
            </p>
          </div>
        )}

        {activeSubTab === 'emergency' && (
          <div className={styles.placeholder}>
            <h3 className={styles.placeholderTitle}>
              {t('communityManagement.emergencyTitle')}
            </h3>
            <p className={styles.placeholderText}>
              {t('communityManagement.emergencyDescription')}
            </p>
            <p className={styles.placeholderPhase}>
              {t('communityManagement.phasePlaceholder', { defaultValue: 'Phase 5.' })}
            </p>
          </div>
        )}

        {/* Structures + Metadata only render for org communities — see the
            header doc-comment and the cardItems filter above. */}
        {activeSubTab === 'structures' && isOrg && (
          <div className={styles.placeholder}>
            <h3 className={styles.placeholderTitle}>
              {t('communityManagement.structuresTitle')}
            </h3>
            <p className={styles.placeholderText}>
              {t('communityManagement.structuresDescription')}
            </p>
            <p className={styles.placeholderPhase}>
              {t('communityManagement.phasePlaceholder', { defaultValue: 'Phase 3.' })}
            </p>
          </div>
        )}

        {activeSubTab === 'metadata' && isOrg && (
          <div className={styles.placeholder}>
            <h3 className={styles.placeholderTitle}>
              {t('communityManagement.metadataTitle')}
            </h3>
            <p className={styles.placeholderText}>
              {t('communityManagement.metadataDescription')}
            </p>
            <p className={styles.placeholderPhase}>
              {t('communityManagement.phasePlaceholder', { defaultValue: 'Phase 4.' })}
            </p>
          </div>
        )}

        {activeSubTab === 'playbooks' && isOrg && (
          <CommunityPlaybooksManagement communityGuid={communityGuid} isAdmin={isAdmin} />
        )}
      </div>

      {!isAdmin && (
        <div className={styles.viewerNote}>
          {t('communityManagement.viewerReadOnlyNote')}
        </div>
      )}
    </div>
  );
};

export default CommunityManagementTab;
